/** Accounts payable (§4.8): raise, approve, disapprove, with attachments. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, Eye, FileUp, Paperclip, Plus, RotateCcw, Search, X } from 'lucide-react';
import { api, ApiError, getAccessToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
  type ChipTone,
} from '../../components/ui';

type ApStatus = 'PENDING' | 'COMPLETED' | 'DISAPPROVED' | 'CANCELLED';

/** Enough of a supplier to pay them without opening another screen. */
interface SupplierBrief {
  id: string;
  code: string;
  name: string;
  bankAccountName: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankDetails: string | null;
  tin: string | null;
}

interface ApRow {
  id: string;
  code: string;
  entryDate: string;
  payee: string;
  description: string | null;
  referenceNo: string | null;
  amount: string;
  dueDate: string | null;
  chargeType: 'PROJECT' | 'ACCOUNT';
  supplier: SupplierBrief | null;
  purchaseOrder: { id: string; code: string } | null;
  /** A deal commission raised when its project was completed: amount is the net payable. */
  isCommission?: boolean;
  grossAmount?: string | null;
  ewtRate?: string | null;
  ewtAmount?: string | null;
  status: ApStatus;
  paidDate: string | null;
  remarks: string | null;
  project: { id: string; code: string; name: string } | null;
  chargeAccount: { id: string; code: string; label: string } | null;
  requester: { id: string; name: string };
  attachments: {
    id: string;
    kind: 'SUPPORT' | 'PAYMENT_PROOF';
    file: { originalName: string; mimeType: string; size: number };
  }[];
  _count: { attachments: number };
}

/**
 * Attachments are served through a permission-checked endpoint, so they cannot
 * simply be linked to — the access token has to travel in a header. Fetch it,
 * then hand the browser a blob: open it in a tab, or save it.
 */
async function openAttachment(
  attachmentId: string,
  inline: boolean,
  filename?: string,
): Promise<void> {
  const response = await fetch(
    `/api/v1/payables/attachments/${attachmentId}/download?inline=${String(inline)}`,
    {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    },
  );
  if (!response.ok) return;
  const url = URL.createObjectURL(await response.blob());
  if (inline) {
    window.open(url, '_blank', 'noopener');
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename ?? 'attachment';
    link.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const hasProof = (row: ApRow): boolean =>
  row.attachments.some((attachment) => attachment.kind === 'PAYMENT_PROOF');

interface ProjectOption {
  id: string;
  code: string;
  name: string;
}

interface ParameterGroup {
  key: string;
  values: { id: string; code: string; label: string }[];
}

const TONES: Record<ApStatus, ChipTone> = {
  PENDING: 'warning',
  COMPLETED: 'ok',
  DISAPPROVED: 'critical',
  CANCELLED: 'neutral',
};

export function PayablesPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ q: '', status: '', chargeType: '' });
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState<ApRow | null>(null);
  const [uploading, setUploading] = useState<ApRow | null>(null);
  // New uploads default to the proof, because that is what blocks completion.
  const [uploadKind, setUploadKind] = useState<'SUPPORT' | 'PAYMENT_PROOF'>('PAYMENT_PROOF');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [form, setForm] = useState({
    entryDate: new Date().toISOString().slice(0, 10),
    payee: '',
    description: '',
    referenceNo: '',
    amount: '',
    vatIncluded: true,
    dueDate: '',
    chargeType: 'PROJECT' as 'PROJECT' | 'ACCOUNT',
    projectId: '',
    chargeAccountId: '',
    supplierId: '',
  });
  /** Which payable's documents are open for viewing. */
  const [viewing, setViewing] = useState<ApRow | null>(null);
  // Finance clicks the payee to read the bank details while keying a transfer.
  const [paying, setPaying] = useState<SupplierBrief | null>(null);
  const [completion, setCompletion] = useState({
    paidDate: new Date().toISOString().slice(0, 10),
    paymentModeId: '',
    paymentRef: '',
    remarks: '',
  });

  const params = new URLSearchParams({ pageSize: '50' });
  if (filters.q) params.set('q', filters.q);
  if (filters.status) params.set('status', filters.status);
  if (filters.chargeType) params.set('chargeType', filters.chargeType);

  const payables = useQuery({
    queryKey: ['payables', params.toString()],
    queryFn: () =>
      api<{
        data: ApRow[];
        total: number;
        totals: { byStatus: { status: string; amount: string }[]; grand: string };
      }>(`/payables?${params.toString()}`),
  });

  const projects = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api<{ data: ProjectOption[] }>('/projects?pageSize=200'),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () =>
      api<{ data: { id: string; name: string }[] }>(
        '/suppliers?status=ACTIVE&pageSize=200&sort=name',
      ),
  });

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
  });

  const chargeAccounts = parameters.data?.data.find((g) => g.key === 'charge_account')?.values ?? [];
  const paymentModes = parameters.data?.data.find((g) => g.key === 'payment_mode')?.values ?? [];

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['payables'] });
    void queryClient.invalidateQueries({ queryKey: ['project'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api<{ data: ApRow }>('/payables', {
        method: 'POST',
        body: {
          entryDate: form.entryDate,
          payee: form.payee.trim(),
          description: form.description || undefined,
          referenceNo: form.referenceNo || undefined,
          amount: Number(form.amount || 0),
          vatIncluded: form.vatIncluded,
          dueDate: form.dueDate || undefined,
          chargeType: form.chargeType,
          projectId: form.chargeType === 'PROJECT' ? form.projectId : undefined,
          chargeAccountId: form.chargeType === 'ACCOUNT' ? form.chargeAccountId : undefined,
          supplierId: form.supplierId || undefined,
        },
      }),
    onSuccess: (result) => {
      setError(null);
      setCreating(false);
      setNotice(`${result.data.code} raised and waiting for Finance.`);
      setForm({ ...form, payee: '', description: '', referenceNo: '', amount: '', supplierId: '' });
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not raise the payable.'),
  });

  const complete = useMutation({
    mutationFn: (entry: ApRow) =>
      api(`/payables/${entry.id}/complete`, {
        method: 'POST',
        body: {
          paidDate: completion.paidDate,
          paymentModeId: completion.paymentModeId || undefined,
          paymentRef: completion.paymentRef || undefined,
          remarks: completion.remarks || undefined,
        },
      }),
    onSuccess: () => {
      setError(null);
      setCompleting(null);
      setNotice('Payable completed. The project cost now includes it.');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not complete the payable.'),
  });

  const disapprove = useMutation({
    mutationFn: (input: { id: string; remarks: string }) =>
      api(`/payables/${input.id}/disapprove`, {
        method: 'POST',
        body: { remarks: input.remarks },
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Payable disapproved.');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not disapprove.'),
  });

  const reopen = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      api(`/payables/${input.id}/reopen`, { method: 'POST', body: { reason: input.reason } }),
    onSuccess: () => {
      setError(null);
      setNotice('Payable reopened.');
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not reopen.'),
  });

  const relabel = useMutation({
    mutationFn: (input: { attachmentId: string; kind: 'SUPPORT' | 'PAYMENT_PROOF' }) =>
      api(`/payables/attachments/${input.attachmentId}`, {
        method: 'PATCH',
        body: { kind: input.kind },
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Marked as proof of payment. The payable can be completed now.');
      setUploading(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change the document.'),
  });

  const upload = useMutation({
    mutationFn: async (input: { id: string; file: File; kind: string }) => {
      const body = new FormData();
      body.append('files', input.file);
      const response = await fetch(`/api/v1/payables/${input.id}/attachments?kind=${input.kind}`, {
        method: 'POST',
        credentials: 'include',
        headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
        body,
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new ApiError(response.status, payload?.message ?? 'Upload failed.');
      }
      return response.json();
    },
    onSuccess: (_result, input) => {
      setError(null);
      setNotice(
        input.kind === 'PAYMENT_PROOF'
          ? 'Proof of payment attached. The payable can be completed now.'
          : 'Supporting document attached.',
      );
      setUploading(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not upload the document.'),
  });

  const totalsByStatus = payables.data?.totals.byStatus ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Accounts payable</h1>
          <p className="text-sm text-slate-500">
            What we owe, charged to a project or to a charge account. Completing one needs proof of
            payment.
          </p>
        </div>
        {can('ap.create') && (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" className="h-4 w-4" /> New payable
          </Button>
        )}
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      {totalsByStatus.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {totalsByStatus.map((total) => (
            <Card key={total.status}>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {total.status.charAt(0) + total.status.slice(1).toLowerCase()}
              </p>
              <p className="tabular text-lg font-bold">{peso(total.amount)}</p>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search payee, reference or description"
              value={filters.q}
              onChange={(event) => setFilters({ ...filters, q: event.target.value })}
            />
          </div>
          <select
            className={`${inputClass} w-40`}
            value={filters.status}
            onChange={(event) => setFilters({ ...filters, status: event.target.value })}
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="COMPLETED">Completed</option>
            <option value="DISAPPROVED">Disapproved</option>
          </select>
          <select
            className={`${inputClass} w-44`}
            value={filters.chargeType}
            onChange={(event) => setFilters({ ...filters, chargeType: event.target.value })}
          >
            <option value="">Any charge</option>
            <option value="PROJECT">Charged to a project</option>
            <option value="ACCOUNT">Charge account</option>
          </select>
        </div>

        {payables.isLoading && <Skeleton className="h-64 w-full" />}
        {payables.data?.data.length === 0 && (
          <EmptyState title="Nothing payable here" description="Raise one to record a cost." />
        )}

        {(payables.data?.data.length ?? 0) > 0 && (
          <TableWrap>
            <thead>
              <tr>
                <Th>Entry</Th>
                <Th>Payee</Th>
                <Th>Charged to</Th>
                <Th>Requested by</Th>
                <Th align="right">Amount</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {payables.data?.data.map((row) => (
                <tr key={row.id}>
                  <Td>
                    <p className="font-mono text-xs">{row.code}</p>
                    <p className="text-xs text-slate-500">{shortDate(row.entryDate)}</p>
                  </Td>
                  <Td>
                    {row.supplier ? (
                      <button
                        type="button"
                        className="font-medium text-brand hover:underline"
                        title="Show bank details for payment"
                        onClick={() => setPaying(row.supplier)}
                      >
                        {row.payee}
                      </button>
                    ) : (
                      <p className="font-medium">{row.payee}</p>
                    )}
                    {row.purchaseOrder && (
                      <p className="font-mono text-xs text-brand">from {row.purchaseOrder.code}</p>
                    )}
                    {row.isCommission && (
                      <p className="text-xs font-medium text-violet-700 dark:text-violet-300">
                        Commission{row.referenceNo ? ` · ${row.referenceNo}` : ''}
                      </p>
                    )}
                    {row.description && (
                      <p className="text-xs text-slate-500">{row.description}</p>
                    )}
                    {row._count.attachments > 0 ? (
                      <p className="text-xs">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                          onClick={() => setViewing(row)}
                        >
                          <Paperclip aria-hidden="true" className="h-3 w-3" />
                          View {row._count.attachments} document
                          {row._count.attachments === 1 ? '' : 's'}
                        </button>
                        {row.status === 'PENDING' && !hasProof(row) && (
                          <span className="ml-1 font-semibold text-amber-600">
                            · no proof of payment yet
                          </span>
                        )}
                      </p>
                    ) : (
                      row.status === 'PENDING' && (
                        <p className="text-xs font-semibold text-amber-600">
                          No documents attached yet
                        </p>
                      )
                    )}
                  </Td>
                  <Td>
                    {row.chargeType === 'PROJECT'
                      ? (row.project?.name ?? '—')
                      : (row.chargeAccount?.label ?? '—')}
                  </Td>
                  <Td>{row.requester.name}</Td>
                  <Td align="right" className="tabular font-semibold">
                    {peso(row.amount)}
                    {row.isCommission && row.grossAmount && (
                      <p className="text-xs font-normal text-slate-500">
                        gross {peso(row.grossAmount)} · EWT {peso(row.ewtAmount ?? '0')} withheld
                      </p>
                    )}
                  </Td>
                  <Td>
                    <StatusChip tone={TONES[row.status]}>
                      {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                    </StatusChip>
                    {row.paidDate && (
                      <p className="text-xs text-slate-500">paid {shortDate(row.paidDate)}</p>
                    )}
                    {row.remarks && <p className="text-xs text-slate-500">{row.remarks}</p>}
                  </Td>
                  <Td align="right">
                    <div className="flex flex-wrap justify-end gap-1">
                      {can('ap.create') && row.status === 'PENDING' && (
                        <Button size="sm" variant="ghost" onClick={() => setUploading(row)}>
                          <FileUp aria-hidden="true" className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {can('ap.approve') && row.status === 'PENDING' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              setCompleting(row);
                              setCompletion({
                                paidDate: new Date().toISOString().slice(0, 10),
                                paymentModeId: '',
                                paymentRef: '',
                                remarks: '',
                              });
                            }}
                          >
                            <Check aria-hidden="true" className="h-3.5 w-3.5" /> Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const remarks = window.prompt('Why is this disapproved?');
                              if (remarks) disapprove.mutate({ id: row.id, remarks });
                            }}
                          >
                            <X aria-hidden="true" className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                      {can('ap.approve') && row.status !== 'PENDING' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const reason = window.prompt('Why reopen this payable?');
                            if (reason) reopen.mutate({ id: row.id, reason });
                          }}
                        >
                          <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 font-semibold dark:border-slate-700">
                <Td>Total</Td>
                <Td>{''}</Td>
                <Td>{''}</Td>
                <Td>{''}</Td>
                <Td align="right" className="tabular">
                  {peso(payables.data?.totals.grand)}
                </Td>
                <Td>{''}</Td>
                <Td>{''}</Td>
              </tr>
            </tfoot>
          </TableWrap>
        )}
      </Card>

      {creating && (
        <Modal title="New payable" onClose={() => setCreating(false)}>
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Entry date" required>
              <input
                className={inputClass}
                type="date"
                required
                value={form.entryDate}
                onChange={(event) => setForm({ ...form, entryDate: event.target.value })}
              />
            </Field>
            <Field
              label="Registered supplier"
              hint="Optional — picking one fills in the payee and links this cost to them"
            >
              <select
                className={inputClass}
                value={form.supplierId}
                onChange={(event) => {
                  const supplier = suppliers.data?.data.find(
                    (row) => row.id === event.target.value,
                  );
                  setForm({
                    ...form,
                    supplierId: event.target.value,
                    payee: supplier ? supplier.name : form.payee,
                  });
                }}
              >
                <option value="">Not a registered supplier</option>
                {suppliers.data?.data.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Payee" required hint="A one-off payee can simply be typed in">
              <input
                className={inputClass}
                required
                value={form.payee}
                onChange={(event) => setForm({ ...form, payee: event.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <input
                  className={inputClass}
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </Field>
            </div>
            <Field label="Supplier invoice / reference">
              <input
                className={inputClass}
                value={form.referenceNo}
                onChange={(event) => setForm({ ...form, referenceNo: event.target.value })}
              />
            </Field>
            <Field label="Amount (gross)" required>
              <input
                className={`${inputClass} tabular`}
                type="number"
                step="0.01"
                min={0.01}
                required
                value={form.amount}
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
              />
            </Field>
            <Field label="Due date">
              <input
                className={inputClass}
                type="date"
                value={form.dueDate}
                onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
              />
            </Field>
            <Field label="Charge to" required>
              <select
                className={inputClass}
                value={form.chargeType}
                onChange={(event) =>
                  setForm({ ...form, chargeType: event.target.value as 'PROJECT' | 'ACCOUNT' })
                }
              >
                <option value="PROJECT">A project</option>
                <option value="ACCOUNT">A charge account</option>
              </select>
            </Field>
            <div className="sm:col-span-2">
              {form.chargeType === 'PROJECT' ? (
                <Field label="Project" required>
                  <select
                    className={inputClass}
                    required
                    value={form.projectId}
                    onChange={(event) => setForm({ ...form, projectId: event.target.value })}
                  >
                    <option value="">Choose a project</option>
                    {projects.data?.data.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.code} — {project.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Charge account" required>
                  <select
                    className={inputClass}
                    required
                    value={form.chargeAccountId}
                    onChange={(event) => setForm({ ...form, chargeAccountId: event.target.value })}
                  >
                    <option value="">Choose an account</option>
                    {chargeAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                checked={form.vatIncluded}
                onChange={(event) => setForm({ ...form, vatIncluded: event.target.checked })}
              />
              The amount includes VAT
            </label>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" busy={create.isPending}>
                Raise payable
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {viewing && (
        <Modal
          title={`Documents on ${viewing.code}`}
          description={`${viewing.payee} · ${peso(viewing.amount)}`}
          onClose={() => setViewing(null)}
        >
          <ul className="space-y-2">
            {viewing.attachments.map((attachment) => (
              <li
                key={attachment.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm dark:border-slate-700"
              >
                <span className="min-w-0 flex-1 truncate" title={attachment.file.originalName}>
                  {attachment.file.originalName}
                </span>
                <StatusChip tone={attachment.kind === 'PAYMENT_PROOF' ? 'ok' : 'neutral'}>
                  {attachment.kind === 'PAYMENT_PROOF' ? 'Proof of payment' : 'Supporting'}
                </StatusChip>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void openAttachment(attachment.id, true)}
                >
                  <Eye aria-hidden="true" className="h-4 w-4" /> View
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    void openAttachment(attachment.id, false, attachment.file.originalName)
                  }
                >
                  <Download aria-hidden="true" className="h-4 w-4" /> Save
                </Button>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-between gap-2">
            {can('ap.create') && viewing.status === 'PENDING' ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setUploading(viewing);
                  setViewing(null);
                }}
              >
                <FileUp aria-hidden="true" className="h-4 w-4" /> Add another
              </Button>
            ) : (
              <span />
            )}
            <Button variant="secondary" onClick={() => setViewing(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}

      {paying && (
        <Modal
          title={paying.name}
          description="Where to send this payment."
          onClose={() => setPaying(null)}
        >
          <dl className="space-y-3 text-sm">
            {[
              ['Account name', paying.bankAccountName],
              ['Bank', paying.bankName],
              ['Account number', paying.bankAccountNumber],
              ['Other details', paying.bankDetails],
              ['TIN', paying.tin],
            ]
              .filter(([, value]) => Boolean(value))
              .map(([label, value]) => (
                <div key={label as string}>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
                  <dd className="select-all font-medium">{value}</dd>
                </div>
              ))}
            {!paying.bankAccountName &&
              !paying.bankName &&
              !paying.bankAccountNumber &&
              !paying.bankDetails && (
                <Alert tone="warning">
                  No bank details recorded for this supplier yet. Add them under Suppliers so the
                  next payment does not need chasing.
                </Alert>
              )}
          </dl>
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={() => setPaying(null)}>
              Close
            </Button>
          </div>
        </Modal>
      )}

      {completing && (
        <Modal
          title={`Complete ${completing.code}`}
          description={`${peso(completing.amount)} to ${completing.payee}.`}
          onClose={() => setCompleting(null)}
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              complete.mutate(completing);
            }}
          >
            {!hasProof(completing) && (
              <Alert tone="warning">
                <p className="font-semibold">Proof of payment is missing.</p>
                <p className="mt-1">
                  {completing.attachments.length > 0
                    ? 'This payable has documents, but none is marked as the proof of payment.'
                    : 'Attach the bank slip, transfer receipt or cheque image first.'}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  onClick={() => {
                    const target = completing;
                    setCompleting(null);
                    setUploadKind('PAYMENT_PROOF');
                    setUploading(target);
                  }}
                >
                  <FileUp aria-hidden="true" className="h-3.5 w-3.5" /> Open documents
                </Button>
              </Alert>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Paid date" required>
                <input
                  className={inputClass}
                  type="date"
                  required
                  value={completion.paidDate}
                  onChange={(event) =>
                    setCompletion({ ...completion, paidDate: event.target.value })
                  }
                />
              </Field>
              <Field label="Payment mode">
                <select
                  className={inputClass}
                  value={completion.paymentModeId}
                  onChange={(event) =>
                    setCompletion({ ...completion, paymentModeId: event.target.value })
                  }
                >
                  <option value="">Not set</option>
                  {paymentModes.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Payment reference">
                <input
                  className={inputClass}
                  value={completion.paymentRef}
                  onChange={(event) =>
                    setCompletion({ ...completion, paymentRef: event.target.value })
                  }
                />
              </Field>
              <Field label="Remarks">
                <input
                  className={inputClass}
                  value={completion.remarks}
                  onChange={(event) => setCompletion({ ...completion, remarks: event.target.value })}
                />
              </Field>
            </div>
            <div className="flex gap-2">
              <Button type="submit" busy={complete.isPending} disabled={!hasProof(completing)}>
                Mark completed
              </Button>
              <Button type="button" variant="secondary" onClick={() => setCompleting(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {uploading && (
        <Modal
          title={`Documents for ${uploading.code}`}
          description="PDF, JPG or PNG up to 15 MB each."
          onClose={() => setUploading(null)}
        >
          <div className="space-y-5">
            {uploading.attachments.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-semibold">Already attached</p>
                <ul className="space-y-2">
                  {uploading.attachments.map((attachment) => (
                    <li
                      key={attachment.id}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm dark:border-slate-700"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {attachment.file.originalName}
                      </span>
                      {attachment.kind === 'PAYMENT_PROOF' ? (
                        <StatusChip tone="ok">Proof of payment</StatusChip>
                      ) : (
                        <>
                          <StatusChip tone="neutral">Supporting</StatusChip>
                          <Button
                            size="sm"
                            variant="secondary"
                            busy={relabel.isPending}
                            onClick={() =>
                              relabel.mutate({
                                attachmentId: attachment.id,
                                kind: 'PAYMENT_PROOF',
                              })
                            }
                          >
                            Use as proof
                          </Button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className="mb-2 text-sm font-semibold">Attach another</p>
              <Field
                label="What is this document?"
                hint="A payable can only be completed once one document is the proof of payment."
              >
                <div className="space-y-2">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="attachment-kind"
                      className="mt-1 h-4 w-4 border-slate-300 text-navy focus:ring-brand"
                      checked={uploadKind === 'PAYMENT_PROOF'}
                      onChange={() => setUploadKind('PAYMENT_PROOF')}
                    />
                    <span>
                      <strong>Proof of payment</strong> — the bank slip, transfer receipt or cheque
                      image. Required before this payable can be completed.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="attachment-kind"
                      className="mt-1 h-4 w-4 border-slate-300 text-navy focus:ring-brand"
                      checked={uploadKind === 'SUPPORT'}
                      onChange={() => setUploadKind('SUPPORT')}
                    />
                    <span>
                      <strong>Supporting document</strong> — the supplier invoice, quotation or
                      delivery receipt.
                    </span>
                  </label>
                </div>
              </Field>

              <label className="mt-3 block rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm dark:border-slate-700">
                Choose a file to upload as{' '}
                <strong>
                  {uploadKind === 'PAYMENT_PROOF' ? 'proof of payment' : 'a supporting document'}
                </strong>
                <input
                  type="file"
                  className="mt-2 w-full text-xs"
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) upload.mutate({ id: uploading.id, file, kind: uploadKind });
                    event.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
